import { createFileRoute } from '@tanstack/react-router'
import { Button, DatePicker, Form, Space, Spin, Table, Typography, Tag } from 'antd'
import { useAnalyticsSearchParams } from '../hooks/useUrlSearchParams'
import { useUserActions } from '../hooks/useAnalytics'
import { ACTION_DESCRIPTIONS } from '../constants/action-types'
import {
  getActionResultDescription,
  getTargetTypeDescription
} from '../constants/enums'
import type { ColumnsType } from 'antd/es/table'
import type { UserActionRecord } from '../types/analytics'
import type { TimeRangePickerProps } from 'antd'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Title } = Typography
const FAVORITE_LIST_ID = 'favoriteList'
const FAVORITE_LIST_NAME = '收藏列表'
const FAVORITE_LIST_VIEW_NAME = '查看'+FAVORITE_LIST_NAME

// 预设时间范围配置
const rangePresets: TimeRangePickerProps['presets'] = [
  { label: '最近1小时', value: [dayjs().add(-1, 'h'), dayjs()] },
  { label: '最近6小时', value: [dayjs().add(-6, 'h'), dayjs()] },
  { label: '最近12小时', value: [dayjs().add(-12, 'h'), dayjs()] },
  { label: '最近24小时', value: [dayjs().add(-24, 'h'), dayjs()] },
  { label: '最近48小时', value: [dayjs().add(-48, 'h'), dayjs()] },
  { label: '最近7天', value: [dayjs().add(-7, 'd'), dayjs()] },
  { label: '最近14天', value: [dayjs().add(-14, 'd'), dayjs()] },
  { label: '最近30天', value: [dayjs().add(-30, 'd'), dayjs()] },
  { label: '最近90天', value: [dayjs().add(-90, 'd'), dayjs()] },
]
function isFavoriteListId(targetId:string):boolean{
  return targetId===FAVORITE_LIST_ID
}
export const Route = createFileRoute('/analytics')({
  component: AnalyticsPage,
})

function AnalyticsPage() {
  const [form] = Form.useForm()
  const { searchParams, setSearchParams } = useAnalyticsSearchParams()

  const { data, isLoading, refetch } = useUserActions(searchParams)

  const handleSearch = (values: any) => {
    const timeRange = values.timeRange
    if (timeRange && timeRange.length === 2) {
      setSearchParams(prev => ({
        ...prev,
        startTime: timeRange[0].unix() * 1000, // 转换为毫秒时间戳
        endTime: timeRange[1].unix() * 1000,
        pageNum: 1,
      }))
    }
  }

  const handlePageChange = (page: number, pageSize?: number) => {
    setSearchParams(prev => ({
      ...prev,
      pageNum: page,
      pageSize: pageSize || prev.pageSize,
    }))
  }

  
  // 表格列定义
  const columns: ColumnsType<UserActionRecord> = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (timestamp: number) => new Date(timestamp).toLocaleString('zh-CN'),
    },
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 200,
      ellipsis: true,
    },
    {
      title: '行为类型',
      dataIndex: 'actionType',
      key: 'actionType',
      width: 150,
      render: (actionType: string,record) => (
        <span title={isFavoriteListId(record.targetId)?FAVORITE_LIST_VIEW_NAME:actionType}>
          {isFavoriteListId(record.targetId)?FAVORITE_LIST_VIEW_NAME:ACTION_DESCRIPTIONS[actionType] || actionType}
        </span>
      ),
    },
    {
      title: '目标类型',
      dataIndex: 'targetType',
      key: 'targetType',
      width: 120,
      render: (targetType: string,record) => {
        if(isFavoriteListId(record.targetId)){
          return <span title={FAVORITE_LIST_NAME}>{FAVORITE_LIST_NAME}</span>;
        }
        const description = getTargetTypeDescription(targetType);
        return <span title={targetType}>{description}</span>;
      },
    },
    {
      title: '目标ID',
      dataIndex: 'targetId',
      key: 'targetId',
      width: 150,
      ellipsis: true,
      render:(targetId:string)=>{
        if(isNaN(Number(targetId))){
          return <span>-</span>;
        }
        return <span title={targetId}>{targetId}</span>;
      }
    },
    {
      title: '结果',
      dataIndex: 'result',
      key: 'result',
      width: 100,
      render: (result: string) => {
        const description = getActionResultDescription(result);
        const color = result === 'SUCCESS' ? 'green' : 'red';
        return <Tag color={color}>{description}</Tag>;
      },
    },
    {
      title: '耗时(ms)',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
    },
    {
      title: '客户端IP',
      dataIndex: 'clientIp',
      key: 'clientIp',
      width: 140,
    },
    {
      title: '错误信息',
      dataIndex: 'errorMsg',
      key: 'errorMsg',
      width: 200,
      ellipsis: true,
      render: (errorMsg: string | null) => errorMsg ? (
        <span style={{ color: '#ff4d4f' }}>{errorMsg}</span>
      ) : '-',
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>行为日志</Title>

      {/* 搜索表单 */}
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: '16px' }}
        onFinish={handleSearch}
        initialValues={{
          timeRange: [
            dayjs(searchParams.startTime),
            dayjs(searchParams.endTime),
          ],
        }}
      >
        <Form.Item name="timeRange" label="时间范围">
          <RangePicker
            showTime
            presets={rangePresets}
            format="YYYY/MM/DD HH:mm:ss"
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              查询
            </Button>
            <Button onClick={() => refetch()}>
              刷新
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* 数据表格 */}
      <Spin spinning={isLoading}>
        <Table
          columns={columns}
          dataSource={data?.records || []}
          rowKey={(record) => `${record.timestamp}-${record.userId}`}
          pagination={{
            current: searchParams.pageNum,
            pageSize: searchParams.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Spin>
    </div>
  )
}